import React from "react";
import { Grid } from "@purpur/grid";
import { Heading } from "@purpur/heading";
import { Paragraph } from "@purpur/paragraph";
import { useTheme } from "@purpur/theme";
import c from "classnames/bind";

import styles from "./hero-banner.module.scss";
import { HeroBannerActions } from "./hero-banner-actions";
import { HeroBannerMedia } from "./hero-banner-media";
import type { HeroBannerProps } from "./hero-banner-types";

export type { HeroBannerMediaProps } from "./hero-banner-media";
export type { HeroBannerProps, TitleHyphens } from "./hero-banner-types";
export { TitleHyphenVariant } from "./hero-banner-types";

const cx = c.bind(styles);

const rootClassName = "purpur-hero-banner";

export const HeroBanner = ({
  children,
  className,
  breadcrumbs,
  forceColorScheme,
  beforeTitle,
  title,
  titleTag = "h1",
  titleHyphens = "default",
  preamble,
  additionalText,
  media,
  ...props
}: HeroBannerProps) => {
  const { options } = useTheme();
  const classes = cx([rootClassName, className]);

  const hasMedia = Boolean(media);
  const hasBreadcrumbs = Boolean(breadcrumbs);
  const hasAdditionalText = Boolean(additionalText);

  const resolvedColorScheme = forceColorScheme ?? options?.heroBannerColorScheme;

  return (
    <section data-color-scheme={resolvedColorScheme} className={classes} {...props}>
      <Grid
        noGap
        className={cx([
          `${rootClassName}__container`,
          {
            [`${rootClassName}__container--with-media`]: hasMedia,
            [`${rootClassName}__container--no-media`]: !hasMedia,
          },
        ])}
        containerClassName={cx(`${rootClassName}__grid-container`)}
      >
        {breadcrumbs && <div className={cx(`${rootClassName}__breadcrumbs`)}>{breadcrumbs}</div>}

        <div className={cx([`${rootClassName}__media`, `${rootClassName}__media--sm`])}>
          {media}
        </div>

        <div
          className={cx([
            `${rootClassName}__content`,
            {
              [`${rootClassName}__content--no-media`]: !hasMedia,
              [`${rootClassName}__content--no-breadcrumbs`]: !hasBreadcrumbs,
              [`${rootClassName}__content--no-additional-text`]: !hasAdditionalText,
            },
          ])}
        >
          <div
            className={cx([
              `${rootClassName}__main-content`,
              {
                [`${rootClassName}__main-content--no-media`]: !hasMedia,
              },
            ])}
          >
            {beforeTitle}
            {title && (
              <Heading
                negative
                tag={titleTag}
                variant="display-50"
                className={cx([
                  `${rootClassName}__title`,
                  `${rootClassName}__title--hyphens-${titleHyphens}`,
                ])}
              >
                {title}
              </Heading>
            )}
            {preamble && (
              <Paragraph
                negative
                variant="preamble-100"
                className={cx(`${rootClassName}__preamble`)}
              >
                {preamble}
              </Paragraph>
            )}
            {children && <div className={cx(`${rootClassName}__custom-content`)}>{children}</div>}
          </div>

          {additionalText && (
            <Paragraph
              negative
              variant="additional-100"
              className={styles[`${rootClassName}__additional-text`]}
            >
              {additionalText}
            </Paragraph>
          )}
        </div>

        <div className={cx([`${rootClassName}__media`, `${rootClassName}__media--lg`])}>
          {media}
        </div>
      </Grid>
    </section>
  );
};

HeroBanner.displayName = "HeroBanner";

HeroBanner.Media = HeroBannerMedia;
HeroBanner.Actions = HeroBannerActions;
